// [PURPOSE]: Warlock Pact Boon + Eldritch Invocation data (SRD-safe subset).
// [WHY]: Warlock players previously had no UI to record their Pact Boon choice
//        (level 3) or Eldritch Invocations (levels 2/5/7/9/12/15/18). Features
//        existed in srdCharacterData.js:1585-1601 but no interactive picker.
// [INTEGRATION]: Consumed by WarlockPactPanel. Choices stored on
//        playerStats.classes[warlockIdx] as `pactBoon: string` and
//        `invocations: string[]` (arrays of invocation ids).
// [EFFECTS — Phase 4b Apr 2026]: Invocations with an `effects` array are
//        auto-registered by WarlockPactPanel on mount (see its useEffect loop
//        calling effectRegistry.register). Narrative-only invocations that
//        cannot be expressed as passive stat effects (at-will spells, once-per-rest
//        resources, per-hit combat modifiers) carry `systemSupport: { supported: false, reason }`
//        instead. See docs/superpowers/plans/2026-04-23-phase4b-1-warlock-invocations.md
//        for the full audit classification.

/**
 * Pact Boon options (PHB p.107-108 + TCoE Pact of the Talisman).
 * Chosen at Warlock level 3.
 *
 * We include all four options. The `source` field lets the Sources panel
 * filter Talisman out if only 2014-PHB content is enabled.
 */
export const PACT_BOONS = Object.freeze({
  chain: {
    id: 'chain',
    name: 'Pact of the Chain',
    icon: '🔗', // 🔗
    source: 'phb',
    summary: 'Summon a familiar in a more powerful form — imp, pseudodragon, quasit, sprite, or sphinx of wonder (XGtE).',
    description:
      "You learn the find familiar spell and can cast it as a ritual. Your familiar can take special forms (imp, pseudodragon, quasit, or sprite) and you can use your action to grant it an attack as part of casting a spell on it, sacrificing one of your own attacks."
  },
  blade: {
    id: 'blade',
    name: 'Pact of the Blade',
    icon: '⚔️', // ⚔️
    source: 'phb',
    summary: 'Conjure a magical pact weapon as a bonus action; bond existing magic weapons to it.',
    description:
      "You can use your action to create a pact weapon in your empty hand — any melee weapon you choose. You can perform a ritual to bind a magic weapon to yourself so that it becomes your pact weapon. You are proficient with any weapon summoned or bound this way."
  },
  tome: {
    id: 'tome',
    name: 'Pact of the Tome',
    icon: '📖', // 📖
    source: 'phb',
    summary: 'Receive a Book of Shadows containing 3 cantrips from any class.',
    description:
      "Your patron gives you a grimoire — the Book of Shadows. Choose 3 cantrips from any class's spell list. While holding the book, you can cast those cantrips at will. If you lose it, a 1-hour ceremony summons a replacement."
  },
  talisman: {
    id: 'talisman',
    name: 'Pact of the Talisman',
    icon: '📿', // 📿
    source: 'tashas',
    summary: 'Receive a protective talisman that grants d4 bonus to failed ability checks.',
    description:
      "Your patron gives you a talisman — a mystic amulet. While worn, a creature gains a bonus d4 to failed ability checks (uses equal to your proficiency bonus per long rest)."
  }
});

/**
 * Eldritch Invocations — SRD-safe subset of the most-played invocations.
 * Each has `minLevel` and optionally `requires` (pact boon id or another
 * invocation id) + `source`. Prerequisites are enforced in the picker.
 *
 * Full list runs 40+ in PHB/XGtE/TCoE; this covers the essentials the
 * majority of Warlock builds use. Custom invocations can be added via the
 * Content Workshop feat creator in a future pass.
 */
export const ELDRITCH_INVOCATIONS = Object.freeze([
  {
    id: 'agonizing_blast',
    name: 'Agonizing Blast',
    minLevel: 2,
    requires: { spell: 'Eldritch Blast' },
    source: 'phb',
    // [Phase 4b]: +CHA mod to damage. Approximation: applies to all damage rolls,
    // not exclusively Eldritch Blast hits (system has no per-attack-type bonus concept).
    // Player-visible consequence: a Warlock hitting with a greatsword will also see +CHA
    // damage from this invocation. RAW limits it to EB only; tighten once per-attack-type
    // damage bonuses are introduced.
    effects: [
      { type: 'damageBonus', operation: 'add', value: 'cha_modifier' },
    ],
    description:
      "When you cast eldritch blast, add your Charisma modifier to the damage it deals on a hit."
  },
  {
    id: 'armor_of_shadows',
    name: 'Armor of Shadows',
    minLevel: 2,
    source: 'phb',
    // [FIX APR 2026 — Phase 4]: Mage armor = 13+DEX vs base 10+DEX = +3 AC, only when unarmored.
    effects: [{ type: 'ac', operation: 'add', value: 3, condition: { when: 'unarmored' } }],
    description: "You can cast mage armor on yourself at will, without expending a spell slot."
  },
  {
    id: 'beast_speech',
    name: 'Beast Speech',
    minLevel: 2,
    source: 'phb',
    // [Phase 4b-CW]: At-will spell grant — registered via effectRegistry, consumed by
    // SpellsPanel (renders ∞) and spellCastingService (skips slot consumption).
    effects: [
      { type: 'atWillSpell', operation: 'grant', value: 'Speak with Animals' },
    ],
    description: "You can cast speak with animals at will, without expending a spell slot."
  },
  {
    id: 'beguiling_influence',
    name: 'Beguiling Influence',
    minLevel: 2,
    source: 'phb',
    // [Phase 4b]: Proficiency grants — auto-registered by WarlockPactPanel via effectRegistry.
    effects: [
      { type: 'proficiency', operation: 'grant', value: 'skill:deception', proficiencyLevel: 1 },
      { type: 'proficiency', operation: 'grant', value: 'skill:persuasion', proficiencyLevel: 1 },
    ],
    description: "You gain proficiency in the Deception and Persuasion skills."
  },
  {
    id: 'devils_sight',
    name: "Devil's Sight",
    minLevel: 2,
    source: 'phb',
    // [Phase 4b]: Sense grant — 120ft darkvision (magical + nonmagical darkness).
    effects: [
      { type: 'sense', operation: 'grant', target: 'darkvision', value: 120 },
    ],
    description:
      "You can see normally in darkness, both magical and nonmagical, to a distance of 120 feet."
  },
  {
    id: 'eldritch_sight',
    name: 'Eldritch Sight',
    minLevel: 2,
    source: 'phb',
    // [Phase 4b-CW]: At-will spell grant — registered via effectRegistry, consumed by
    // SpellsPanel (renders ∞) and spellCastingService (skips slot consumption).
    effects: [
      { type: 'atWillSpell', operation: 'grant', value: 'Detect Magic' },
    ],
    description: "You can cast detect magic at will, without expending a spell slot."
  },
  {
    id: 'eldritch_spear',
    name: 'Eldritch Spear',
    minLevel: 2,
    requires: { spell: 'Eldritch Blast' },
    source: 'phb',
    // [Phase 4b-infra]: spellModifier — sets Eldritch Blast range to 300 ft (overrides base 120).
    // Consumer: effectCalculator.getSpellRange(playerStats, inventory, 'Eldritch Blast', 120).
    effects: [
      { type: 'spellModifier', target: 'eldritch blast', property: 'range', operation: 'set', value: 300 },
    ],
    description:
      "When you cast eldritch blast, its range is 300 feet (instead of the standard 120)."
  },
  {
    id: 'fiendish_vigor',
    name: 'Fiendish Vigor',
    minLevel: 2,
    source: 'phb',
    // [Phase 4b-CW]: At-will spell grant — registered via effectRegistry, consumed by
    // SpellsPanel (renders ∞) and spellCastingService (skips slot consumption).
    effects: [
      { type: 'atWillSpell', operation: 'grant', value: 'False Life' },
    ],
    description:
      "You can cast false life on yourself at will as a 1st-level spell, without expending a spell slot."
  },
  {
    id: 'mask_of_many_faces',
    name: 'Mask of Many Faces',
    minLevel: 2,
    source: 'phb',
    // [Phase 4b-CW]: At-will spell grant — registered via effectRegistry, consumed by
    // SpellsPanel (renders ∞) and spellCastingService (skips slot consumption).
    effects: [
      { type: 'atWillSpell', operation: 'grant', value: 'Disguise Self' },
    ],
    description: "You can cast disguise self at will, without expending a spell slot."
  },
  {
    id: 'misty_visions',
    name: 'Misty Visions',
    minLevel: 2,
    source: 'phb',
    // [Phase 4b-CW]: At-will spell grant — registered via effectRegistry, consumed by
    // SpellsPanel (renders ∞) and spellCastingService (skips slot consumption).
    effects: [
      { type: 'atWillSpell', operation: 'grant', value: 'Silent Image' },
    ],
    description: "You can cast silent image at will, without expending a spell slot."
  },
  {
    id: 'repelling_blast',
    name: 'Repelling Blast',
    minLevel: 2,
    requires: { spell: 'Eldritch Blast' },
    source: 'phb',
    // [Phase 4b-infra]: spellModifier — Eldritch Blast hits push 10 ft.
    // Consumer: effectCalculator.getSpellModifier(playerStats, inventory, 'Eldritch Blast', 'knockback').
    // Combat UI reads this to surface "Pushes 10 ft" on EB hit results.
    effects: [
      { type: 'spellModifier', target: 'eldritch blast', property: 'knockback', operation: 'set', value: 10 },
    ],
    description:
      "When you hit a creature with eldritch blast, you can push the creature up to 10 feet away from you in a straight line."
  },
  {
    id: 'thief_of_five_fates',
    name: 'Thief of Five Fates',
    minLevel: 2,
    source: 'phb',
    // [Phase 4b-audit Apr 2026]: Adds Bane to known Warlock spells via 'learnedSpell'.
    // RAW limits to once per long rest using a Warlock slot — usage limit isn't yet
    // enforced (separate task: per-rest resource tracking). Player MUST self-track for now.
    effects: [
      { type: 'learnedSpell', operation: 'grant', value: 'Bane' },
    ],
    description:
      "You can cast bane once using a warlock spell slot. You can't do so again until you finish a long rest."
  },
  {
    id: 'book_of_ancient_secrets',
    name: 'Book of Ancient Secrets',
    minLevel: 2,
    requires: { pactBoon: 'tome' },
    source: 'phb',
    // [Phase 4b-audit Apr 2026]: Feature flag for ritual-casting from any class.
    // Player picks 2 1st-level ritual-tag spells via the Tome customization UI (separate task).
    // 'inscribe_rituals_any_class' flag: SpellbookTab can offer the ritual-spell picker.
    // 'ritual_book_extension' flag: ritual list grows beyond standard Warlock list.
    effects: [
      { type: 'feature', operation: 'grant', value: 'inscribe_rituals_any_class' },
      { type: 'feature', operation: 'grant', value: 'ritual_book_extension' },
    ],
    description:
      "You can inscribe magical rituals in your Book of Shadows. Choose 2 1st-level spells with the ritual tag from any class's spell list; you can cast them as rituals only. Further rituals can be added as you find scrolls."
  },
  {
    id: 'voice_of_the_chain_master',
    name: 'Voice of the Chain Master',
    minLevel: 2,
    requires: { pactBoon: 'chain' },
    source: 'phb',
    // [Phase 4b-audit Apr 2026]: Narrative feature flags — UI can query
    // effectCalculator.hasFeatureFlag(stats, inv, '<flag>') to surface familiar capabilities.
    effects: [
      { type: 'feature', operation: 'grant', value: 'familiar_telepathy' },
      { type: 'feature', operation: 'grant', value: 'familiar_senses_share' },
      { type: 'feature', operation: 'grant', value: 'speak_through_familiar' },
    ],
    description:
      "You can communicate telepathically with your familiar and perceive through its senses as long as you are on the same plane. You can speak through your familiar in your own voice."
  },
  {
    id: 'thirsting_blade',
    name: 'Thirsting Blade',
    minLevel: 5,
    requires: { pactBoon: 'blade' },
    source: 'phb',
    // [Phase 4b]: Extra attack grant — surfaces via effectCalculator.getExtraAttacks().
    // Note: only applies to pact weapon attacks per RAW; system models as general extra attack.
    effects: [
      { type: 'extra_attack', operation: 'add', value: 1 },
    ],
    description:
      "You can attack with your pact weapon twice, instead of once, whenever you take the Attack action on your turn."
  },
  {
    id: 'lifedrinker',
    name: 'Lifedrinker',
    minLevel: 12,
    requires: { pactBoon: 'blade' },
    source: 'phb',
    // [Phase 4b]: +CHA mod to damage. Approximation: applies to all damage rolls,
    // not exclusively pact-weapon hits (system has no per-weapon-type bonus concept).
    // Player-visible consequence: a Blade-pact Warlock's ranged attacks (e.g., thrown
    // dagger) or spell damage also pick up +CHA. RAW limits it to pact-weapon hits only
    // as necrotic damage; damage-type restriction is also not enforced.
    effects: [
      { type: 'damageBonus', operation: 'add', value: 'cha_modifier' },
    ],
    description:
      "When you hit a creature with your pact weapon, the creature takes extra necrotic damage equal to your Charisma modifier (minimum 1)."
  },
  {
    id: 'aspect_of_the_moon',
    name: 'Aspect of the Moon',
    minLevel: 2,
    requires: { pactBoon: 'tome' },
    source: 'xanathars',
    // [Phase 4b-audit Apr 2026]: Narrative feature flag — surfaces via hasFeatureFlag.
    // 'no_sleep_required' lets the rest UI skip sleep prompts and treat 8 hours of light
    // activity as equivalent to a long rest's sleep portion. 'sleep_immunity' blocks
    // forced-sleep effects from spells like Sleep.
    effects: [
      { type: 'feature', operation: 'grant', value: 'no_sleep_required' },
      { type: 'feature', operation: 'grant', value: 'sleep_immunity' },
    ],
    description:
      "You no longer need to sleep and can't be forced to sleep by any means. To gain the benefits of a long rest, you can spend 8 hours in light activity."
  },
  {
    id: 'gift_of_the_protectors',
    name: 'Gift of the Protectors',
    minLevel: 9,
    requires: { pactBoon: 'tome' },
    source: 'tashas',
    // [Phase 4b-audit Apr 2026]: Feature flag for the "drop to 1 HP" trigger. Combat damage
    // resolution UI can query hasFeatureFlag(stats, inv, 'hp_drop_to_1_trigger') to surface
    // a one-time HP-protection prompt when reducing to 0. The per-rest usage limit is not
    // yet enforced (separate task — needs per-rest resource tracking infrastructure).
    effects: [
      { type: 'feature', operation: 'grant', value: 'hp_drop_to_1_trigger' },
    ],
    description:
      "When you would be reduced to 0 hit points, you instead drop to 1 hit point. Once this triggers, it can't trigger again until you finish a long rest. Usable a number of times per long rest equal to your proficiency bonus if written in your Book of Shadows."
  }
]);

/**
 * Number of Eldritch Invocations known at a given Warlock class level.
 * PHB p.107. Warlock levels only — does not include levels from other classes.
 */
export function invocationsKnownAtLevel(warlockLevel) {
  if (warlockLevel < 2) return 0;
  if (warlockLevel < 5) return 2;
  if (warlockLevel < 7) return 3;
  if (warlockLevel < 9) return 4;
  if (warlockLevel < 12) return 5;
  if (warlockLevel < 15) return 6;
  if (warlockLevel < 18) return 7;
  return 8;
}

/**
 * Filter invocations available to a character given their Warlock level,
 * chosen pact boon, and enabled source list.
 *
 * @param {number} warlockLevel
 * @param {string|null} pactBoon - chosen boon id or null if not yet picked
 * @param {Array<string>} enabledSources - e.g. ['phb', 'xanathars', 'tashas']
 * @param {Array<string>} knownSpells - player's known spells for spell prereq checks
 * @returns {Array} invocations the player is eligible to learn
 */
export function eligibleInvocations(warlockLevel, pactBoon, enabledSources, knownSpells = []) {
  const enabled = new Set(enabledSources || ['phb']);
  return ELDRITCH_INVOCATIONS.filter(inv => {
    if (warlockLevel < inv.minLevel) return false;
    if (!enabled.has(inv.source)) return false;
    if (inv.requires?.pactBoon && inv.requires.pactBoon !== pactBoon) return false;
    if (inv.requires?.spell) {
      const hasSpell = (knownSpells || []).some(s =>
        String(s.name || s).toLowerCase() === String(inv.requires.spell).toLowerCase()
      );
      if (!hasSpell) return false;
    }
    return true;
  });
}
